import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Download, Mail, Settings } from 'lucide-react';
import {
  COMMISSION_EMAIL_PLACEHOLDERS,
  COMMISSION_EMAIL_PLACEHOLDER_HELP,
  PARTNER_STATEMENT_SEND_FILTERS,
  canOpenContract,
  commissionStatementFileName,
  formatCurrency,
  formatDate,
  formatDateTime,
  sumMoney,
  type CommissionEmailDraft,
  type CommissionEmailSettings,
  type CommissionExclusion,
  type Paginated,
  type PartnerCommissionOverview,
  type PartnerOverviewGroup,
  type PartnerStatement,
  type PartnerStatementSendFilter,
} from '@codepass/shared';
import { ApiError, api, apiUrl } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { cn } from '@/lib/cn';
import { contractPath } from '@/lib/paths';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Modal,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from '@/components/ui';
import {
  EmptyState,
  Money,
  PageHeader,
  Pagination,
  PermittedLink,
  RefreshButton,
} from '@/components/domain';
import {
  AgreementStatusBadge,
  CommissionLinesTable,
  rateLabel,
} from '@/components/PartnerCommission';
import { SubNav } from '@/pages/Invoices';

/**
 * /fakturering/provision - the settlement of partner commission. The page is 'Se provision'; the
 * email settings, approving (and Send igen) and the Excel files are each their own permission on
 * /admin/roller. The route and the API both check.
 *
 * One row per partner agreement - the referred contracts under it and the three amounts - opening
 * to its commission lines, and "Godkend og send", which turns the paid, unsettled lines into a
 * statement and emails it to the partner as Excel. Below, the statements already approved. The
 * email settings are behind their button in the header.
 *
 * **Not optimistic.** Approving pays a partner and sends an email outside OneCode; the page waits
 * for the answer and shows the statement number and how the send went, rather than guessing.
 *
 * Everything shown is worked out by the server - see domain/partner-settlement.ts.
 */

const QUERY_KEY = ['partner-commission'];

/** Rows per page, in both lists. */
const PAGE_SIZE = 25;

/** PRD par. 5.1: a search waits 300 ms, so typing does not send a call per letter. */
const SEARCH_DEBOUNCE_MS = 300;

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** `a@x.dk, b@x.dk` -> two addresses. Commas, semicolons and spaces all separate. */
function addresses(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function PartnerCommissionPage() {
  /* Siden er 'Se provision'; hver knap er sin egen rettighed - se /admin/roller. */
  const { can } = useAuth();
  const [approving, setApproving] = useState<PartnerOverviewGroup | null>(null);
  const [resending, setResending] = useState<PartnerStatement | null>(null);
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [result, setResult] = useState<{
    tone: 'success' | 'warning';
    text: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<PartnerCommissionOverview>('/partner-commission'),
  });

  return (
    <>
      <PageHeader
        title="Partnerprovision"
        description="Provision til partnere på henviste salg. Betalte linjer godkendes og sendes til partneren som en opgørelse."
        actions={
          <>
            {can('partner_commission.email_settings') ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={!data}
                onClick={() => setEmailSettingsOpen(true)}
              >
                <Settings className="h-3.5 w-3.5" />
                Mailopsætning
              </Button>
            ) : null}
            <RefreshButton prefixes={['partner-commission']} />
          </>
        }
      />
      <SubNav />

      {result ? (
        <Alert tone={result.tone} className="mb-4" onDismiss={() => setResult(null)}>
          {result.text}
        </Alert>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : error || !data ? (
        <Alert tone="error">{errorText(error, 'Kunne ikke hente partnerprovisionen.')}</Alert>
      ) : (
        <div className="space-y-4">
          <SetupAlerts overview={data} />

          {data.groups.length === 0 ? (
            <Card>
              <EmptyState
                title="Ingen salg er henvist via en partner"
                description="Et salg kommer med her, når 'Henvist via partner' er sat på salgsmuligheden i CRM."
              />
            </Card>
          ) : (
            <PartnersCard
              groups={data.groups}
              canApprove={
                data.settlement_ready && data.notes.length === 0 && Boolean(data.email_sender)
              }
              onApprove={can('partner_commission.approve') ? setApproving : null}
            />
          )}

          <StatementsCard onResend={can('partner_commission.approve') ? setResending : null} />
        </div>
      )}

      {emailSettingsOpen && data ? (
        <EmailSettingsModal
          settings={data.email_settings}
          sender={data.email_sender}
          onClose={() => setEmailSettingsOpen(false)}
          onSaved={() => {
            setEmailSettingsOpen(false);
            setResult({ tone: 'success', text: 'Mailopsætningen er gemt.' });
          }}
        />
      ) : null}

      {approving ? (
        <ApproveModal
          group={approving}
          sender={data?.email_sender ?? null}
          onClose={() => setApproving(null)}
          onDone={(text, tone) => {
            setApproving(null);
            setResult({ text, tone });
          }}
        />
      ) : null}
      {resending ? (
        <ResendModal
          statement={resending}
          sender={data?.email_sender ?? null}
          onClose={() => setResending(null)}
          onDone={(text, tone) => {
            setResending(null);
            setResult({ text, tone });
          }}
        />
      ) : null}
    </>
  );
}

/** What is not set up yet, and what could not be read - each with what to do about it. */
function SetupAlerts({ overview }: { overview: PartnerCommissionOverview }) {
  const opensIntegrations = useAuth().can('integrations.view');
  return (
    <>
      {!overview.crm_ready ? (
        <Alert tone="info" title="Partnerfelterne findes ikke i CRM endnu">
          Salgsmulighederne har endnu ikke felterne 'Henvist via partner' og 'Partneraftale' (fase
          A–D i designnotatet). Indtil de er bygget, er intet salg henvist.
        </Alert>
      ) : null}
      {!overview.settlement_ready ? (
        <Alert tone="warning" title="Afregningen er ikke sat op">
          Tabellerne til opgørelserne findes ikke i databasen, så intet kan godkendes. Kør SQL’en i
          docs/partner/afregning-opsaetning.md i Supabase.
        </Alert>
      ) : null}
      {overview.settlement_ready && !overview.email_sender ? (
        <Alert tone="warning" title="Ingen afsender">
          Sæt PARTNER_COMMISSION_SENDER under{' '}
          <PermittedLink
            to="/admin/integrationer"
            allowed={opensIntegrations}
            className="underline"
          >
            Integrationer
          </PermittedLink>
          , før en opgørelse kan sendes.
        </Alert>
      ) : null}
      {overview.notes.length > 0 ? (
        <Alert tone="warning" title="Ikke alle kilder kunne læses — intet kan godkendes">
          <ul className="list-disc space-y-1 pl-5">
            {overview.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
    </>
  );
}

const groupKey = (group: PartnerOverviewGroup): string => group.agreement_id ?? 'none';

/** Exclusions someone has to look at in BC - the rest are settled facts (credited, after the end). */
const NEEDS_CHECK: ReadonlySet<CommissionExclusion> = new Set(['partly_credited', 'unresolved']);

/** What a partner's row says at a glance - and whether it needs anyone now. */
function groupState(group: PartnerOverviewGroup) {
  const problems = group.contracts.flatMap((contract) => contract.problems);
  const toCheck = group.lines.filter(
    (line) => line.settlement === null && line.excluded !== null && NEEDS_CHECK.has(line.excluded),
  ).length;
  const ready = group.ready_line_ids.length;
  const awaiting = group.summary.awaiting_excl_vat > 0;

  /* Ready to settle first, then what is wrong, then what waits for the customer. */
  const rank = ready > 0 ? 0 : problems.length > 0 || toCheck > 0 ? 1 : awaiting ? 2 : 3;
  return { problems, toCheck, ready, rank, quiet: rank === 3 };
}

/** Whether a partner matches the search: its name, PA number, or a referred contract or customer. */
function matchesPartner(group: PartnerOverviewGroup, term: string): boolean {
  const texts = [
    group.agreement?.partner?.name,
    group.agreement?.agreement_no,
    ...group.contracts.flatMap((contract) => [
      contract.contract_no,
      contract.title,
      contract.customer_name,
    ]),
  ];
  return texts.some((text) => text?.toLowerCase().includes(term));
}

/**
 * Every partner agreement as one row - the partner, its referred contracts and the three amounts -
 * with the lines folded away. Built for many partners: folded, a partner is one table row, 25 to a
 * page, with a search on partner, PA number, contract and customer.
 *
 * What needs someone comes first - ready to settle, then problems, then awaiting payment - and
 * partners with nothing open wait behind "Vis alle". A search looks through all of them. Every row
 * starts folded.
 *
 * Paged in the browser: the page has every partner already - the amounts are worked out from all
 * of them - so a page from the server would save nothing.
 */
function PartnersCard({
  groups,
  canApprove,
  onApprove,
}: {
  groups: PartnerOverviewGroup[];
  canApprove: boolean;
  /** Null without 'Godkend provision' - then there is no "Godkend og send". */
  onApprove: ((group: PartnerOverviewGroup) => void) | null;
}) {
  /* Every partner starts folded - also a single one (decided 07-10-2026). */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [showQuiet, setShowQuiet] = useState(false);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  const rows = useMemo(
    () =>
      groups
        .map((group) => ({ group, state: groupState(group) }))
        .sort(
          (a, b) =>
            a.state.rank - b.state.rank ||
            (a.group.agreement?.partner?.name ?? '~').localeCompare(
              b.group.agreement?.partner?.name ?? '~',
              'da',
            ),
        ),
    [groups],
  );
  const quiet = rows.filter((row) => row.state.quiet).length;
  const term = search.trim().toLowerCase();
  /* All quiet: hiding them would leave an empty table. A search looks through everyone. */
  const shown = term
    ? rows.filter((row) => matchesPartner(row.group, term))
    : showQuiet || quiet === rows.length
      ? rows
      : rows.filter((row) => !row.state.quiet);
  const page = shown.slice(offset, offset + PAGE_SIZE);

  const toggle = (key: string): void =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle>Partnere</CardTitle>
        {rows.length > 1 ? (
          <Input
            className="w-64"
            placeholder="Søg på partner, aftale eller kunde"
            aria-label="Søg i partnerne"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
        ) : null}
      </CardHeader>
      <Table>
        <thead>
          <tr>
            <Th>Partner</Th>
            <Th className="w-[200px]">Henviste aftaler</Th>
            {/* In the order the money moves in BC: invoiced, paid, paid out. */}
            <Th className="w-[140px] text-right">Afventer betaling</Th>
            <Th className="w-[140px] text-right">Klar til afregning</Th>
            <Th className="w-[140px] text-right">Afregnet</Th>
            <Th className="w-[170px]" />
          </tr>
        </thead>
        <tbody>
          {page.length === 0 ? (
            <tr>
              <Td colSpan={6} className="text-center text-xs text-ink-muted">
                Ingen partnere matcher søgningen.
              </Td>
            </tr>
          ) : null}
          {page.map(({ group, state }) => (
            <PartnerRow
              key={groupKey(group)}
              group={group}
              state={state}
              open={expanded.has(groupKey(group))}
              onToggle={() => toggle(groupKey(group))}
              canApprove={canApprove}
              onApprove={onApprove ? () => onApprove(group) : null}
            />
          ))}
        </tbody>
        {rows.length > 1 ? (
          <tfoot>
            <tr className="font-medium">
              <Td colSpan={2}>I alt, {rows.length} partnere</Td>
              <Td className="text-right">
                <Money value={sumMoney(groups.map((group) => group.summary.awaiting_excl_vat))} />
              </Td>
              <Td className="text-right">
                <Money value={sumMoney(groups.map((group) => group.summary.ready_excl_vat))} />
              </Td>
              <Td className="text-right">
                <Money value={sumMoney(groups.map((group) => group.summary.settled_excl_vat))} />
              </Td>
              <Td />
            </tr>
          </tfoot>
        ) : null}
      </Table>
      <Pagination total={shown.length} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
      {!term && quiet > 0 && quiet < rows.length ? (
        <div className="border-t border-line px-4 py-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setShowQuiet((value) => !value);
              setOffset(0);
            }}
          >
            {showQuiet
              ? 'Skjul partnere uden noget åbent'
              : `Vis alle — ${quiet} partner${quiet === 1 ? '' : 'e'} uden noget åbent`}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function PartnerRow({
  group,
  state,
  open,
  onToggle,
  canApprove,
  onApprove,
}: {
  group: PartnerOverviewGroup;
  state: ReturnType<typeof groupState>;
  open: boolean;
  onToggle: () => void;
  canApprove: boolean;
  onApprove: (() => void) | null;
}) {
  const agreement = group.agreement;
  const contractNos = group.contracts.map((contract) => contract.contract_no ?? contract.title);

  return (
    <>
      <Tr className="cursor-pointer" onClick={onToggle}>
        <Td>
          <button
            type="button"
            className="flex items-start gap-1.5 text-left"
            aria-expanded={open}
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
          >
            <ChevronRight
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0 text-ink-muted transition-transform',
                open && 'rotate-90',
              )}
              aria-hidden
            />
            <span>
              <span className="font-medium">
                {agreement?.partner?.name ?? 'Henvist uden partneraftale'}
              </span>
              <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted">
                {agreement?.agreement_no ? (
                  <span className="tabular">{agreement.agreement_no}</span>
                ) : null}
                {agreement ? <AgreementStatusBadge agreement={agreement} /> : null}
                {state.problems.length > 0 ? <Badge tone="danger">Fejl</Badge> : null}
                {state.toCheck > 0 ? <Badge tone="warning">Kontrollér i BC</Badge> : null}
              </span>
            </span>
          </button>
        </Td>
        <Td className="text-xs">
          <span className="font-medium">{contractNos.slice(0, 2).join(', ')}</span>
          {contractNos.length > 2 ? (
            <span className="text-ink-muted"> +{contractNos.length - 2}</span>
          ) : null}
        </Td>
        <Td className="text-right">
          <Money value={group.summary.awaiting_excl_vat} />
        </Td>
        <Td className="text-right">
          <Money value={group.summary.ready_excl_vat} />
          {state.ready > 0 ? (
            <span className="block text-xs text-ink-muted">
              {state.ready} linje{state.ready === 1 ? '' : 'r'}
            </span>
          ) : null}
        </Td>
        <Td className="text-right">
          <Money value={group.summary.settled_excl_vat} />
        </Td>
        <Td className="text-right">
          {onApprove && group.agreement_id && state.ready > 0 ? (
            <Button
              size="sm"
              disabled={!canApprove}
              onClick={(event) => {
                event.stopPropagation();
                onApprove();
              }}
            >
              <Mail className="h-3.5 w-3.5" />
              Godkend og send
            </Button>
          ) : null}
        </Td>
      </Tr>
      {open ? (
        <tr>
          <td colSpan={6} className="border-b border-line bg-canvas/50 p-0">
            <AgreementDetails group={group} problems={state.problems} ready={state.ready} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * One partner, opened: the contact, what is wrong, the referred contracts and the lines.
 *
 * The open lines - paid or awaiting payment, and earning - are shown. Two kinds are folded away:
 * lines that earn nothing (credited, after the end, a missing rate) - they are never settled, so
 * they would otherwise stay on the partner for good - and settled lines, which are history.
 */
function AgreementDetails({
  group,
  problems,
  ready,
}: {
  group: PartnerOverviewGroup;
  problems: string[];
  ready: number;
}) {
  const [showExcluded, setShowExcluded] = useState(false);
  const [showSettled, setShowSettled] = useState(false);
  const { can, user } = useAuth();
  const canExport = can('partner_commission.export');
  /* The agreement lists its contracts by number - their kind is not known here. */
  const opensContract = canOpenContract(user, null);
  const agreement = group.agreement;

  const unsettled = group.lines.filter((line) => line.settlement === null);
  const open = unsettled.filter((line) => line.excluded === null);
  const excluded = unsettled.filter((line) => line.excluded !== null);
  const settled = group.lines.filter((line) => line.settlement !== null);

  return (
    <div>
      <div className="space-y-3 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-muted">
          <span className="flex flex-wrap gap-x-2">
            {agreement?.contact ? (
              <span>
                {agreement.contact.name}
                {agreement.contact.email ? ` · ${agreement.contact.email}` : ' · ingen mail i CRM'}
              </span>
            ) : agreement ? (
              <span>Ingen kontaktperson i CRM</span>
            ) : null}
            {agreement?.end_date ? <span>Ophør pr. {formatDate(agreement.end_date)}</span> : null}
          </span>
          {canExport && group.agreement_id && ready > 0 ? (
            <a href={apiUrl(`/partner-commission/agreements/${group.agreement_id}/excel`)}>
              <Button size="sm" variant="secondary">
                <Download className="h-3.5 w-3.5" />
                Excel (udkast)
              </Button>
            </a>
          ) : null}
        </div>

        {problems.length > 0 ? (
          <Alert tone="error" title="Provisionen kan ikke opgøres fuldt ud">
            <ul className="list-disc space-y-1 pl-5">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <ul className="space-y-0.5 text-xs">
          {group.contracts.map((contract) => (
            <li key={contract.id} className="flex flex-wrap gap-x-2">
              <PermittedLink
                to={contractPath(contract)}
                allowed={opensContract}
                className="font-medium hover:underline"
              >
                {contract.contract_no ?? contract.title}
              </PermittedLink>
              <span className="text-ink-muted">
                {contract.customer_name ?? '—'} · udvikling {rateLabel(contract.hours_rate_pct)} ·
                løbende {rateLabel(contract.recurring_rate_pct)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {group.lines.length === 0 ? (
        <p className="border-t border-line px-4 py-3 text-xs text-ink-muted">
          Ingen bogførte fakturaer på de henviste aftaler endnu.
        </p>
      ) : open.length > 0 ? (
        <CommissionLinesTable lines={open} showContract />
      ) : (
        <p className="border-t border-line px-4 py-3 text-xs text-ink-muted">
          Ingen åbne linjer — alt er afregnet eller giver ikke provision.
        </p>
      )}

      {excluded.length > 0 || settled.length > 0 ? (
        <div className="flex flex-wrap gap-2 border-t border-line px-4 py-2">
          {excluded.length > 0 ? (
            <Button size="sm" variant="ghost" onClick={() => setShowExcluded((value) => !value)}>
              {showExcluded ? 'Skjul' : 'Vis'} linjer uden provision ({excluded.length})
            </Button>
          ) : null}
          {settled.length > 0 ? (
            <Button size="sm" variant="ghost" onClick={() => setShowSettled((value) => !value)}>
              {showSettled ? 'Skjul' : 'Vis'} afregnede linjer ({settled.length})
            </Button>
          ) : null}
        </div>
      ) : null}
      {showExcluded ? <CommissionLinesTable lines={excluded} showContract /> : null}
      {showSettled ? <CommissionLinesTable lines={settled} showContract /> : null}
    </div>
  );
}

/**
 * Where the email goes out from - `PARTNER_COMMISSION_SENDER` on /admin/integrationer. Shown, not
 * edited: the address goes with the Exchange permission, which is scoped to it.
 */
function SenderLine({ sender }: { sender: string | null }) {
  const opensIntegrations = useAuth().can('integrations.view');
  return sender ? (
    <p className="text-xs text-ink-muted">
      Email bliver sendt fra <span className="font-medium text-ink">{sender}</span>
    </p>
  ) : (
    <p className="text-xs text-danger">
      Ingen afsender. Sæt PARTNER_COMMISSION_SENDER under{' '}
      <PermittedLink to="/admin/integrationer" allowed={opensIntegrations} className="underline">
        Integrationer
      </PermittedLink>
      .
    </p>
  );
}

/** The email fields - recipients, copy, subject and text - shared by approve and resend. */
function EmailFields({
  sender,
  draft,
  onChange,
}: {
  sender: string | null;
  draft: { to: string; cc: string; subject: string; body: string };
  onChange: (draft: { to: string; cc: string; subject: string; body: string }) => void;
}) {
  return (
    <div className="space-y-3">
      <SenderLine sender={sender} />
      <div>
        <Label htmlFor="mail-to">Til</Label>
        <Input
          id="mail-to"
          value={draft.to}
          onChange={(event) => onChange({ ...draft, to: event.target.value })}
          placeholder="kontakt@partner.dk"
        />
      </div>
      <div>
        <Label htmlFor="mail-cc">Kopi</Label>
        <Input
          id="mail-cc"
          value={draft.cc}
          onChange={(event) => onChange({ ...draft, cc: event.target.value })}
        />
        <p className="mt-1 text-xs text-ink-muted">Flere adresser adskilles med komma.</p>
      </div>
      <div>
        <Label htmlFor="mail-subject">Emne</Label>
        <Input
          id="mail-subject"
          value={draft.subject}
          onChange={(event) => onChange({ ...draft, subject: event.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="mail-body">Tekst</Label>
        <Textarea
          id="mail-body"
          rows={10}
          value={draft.body}
          onChange={(event) => onChange({ ...draft, body: event.target.value })}
        />
      </div>
    </div>
  );
}

function toFields(draft: CommissionEmailDraft) {
  return {
    to: draft.to.join(', '),
    cc: draft.cc.join(', '),
    subject: draft.subject,
    body: draft.body,
  };
}

function fromFields(fields: ReturnType<typeof toFields>): CommissionEmailDraft {
  return {
    to: addresses(fields.to),
    cc: addresses(fields.cc),
    subject: fields.subject,
    body: fields.body,
  };
}

interface ApproveResponse {
  statement: PartnerStatement;
  send: { sent: boolean; error: string | null };
}

/** The outcome in one sentence - the statement stands either way, only the send can fail. */
function sendOutcome(
  statementNo: string,
  send: { sent: boolean; error: string | null },
  to: string[],
): { text: string; tone: 'success' | 'warning' } {
  return send.sent
    ? { tone: 'success', text: `Opgørelsen ${statementNo} er sendt til ${to.join(', ')}.` }
    : {
        tone: 'warning',
        text: `Opgørelsen ${statementNo} er godkendt, men ikke sendt: ${send.error ?? 'ukendt fejl'} Den kan sendes igen under Opgørelser.`,
      };
}

function ApproveModal({
  group,
  sender,
  onClose,
  onDone,
}: {
  group: PartnerOverviewGroup;
  sender: string | null;
  onClose: () => void;
  onDone: (text: string, tone: 'success' | 'warning') => void;
}) {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState(() =>
    toFields(group.email ?? { to: [], cc: [], subject: '', body: '' }),
  );
  const [error, setError] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: () =>
      api.post<ApproveResponse>(`/partner-commission/agreements/${group.agreement_id}/statements`, {
        line_ids: group.ready_line_ids,
        seen_statement_no: group.next_statement_no,
        email: fromFields(fields),
      }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      /* The contracts' tabs show the lines as settled from now on. */
      void queryClient.invalidateQueries({ queryKey: ['contract'] });
      const outcome = sendOutcome(
        response.statement.statement_no,
        response.send,
        addresses(fields.to),
      );
      onDone(outcome.text, outcome.tone);
    },
    onError: (cause) => setError(errorText(cause, 'Opgørelsen kunne ikke godkendes.')),
  });

  const ready = group.ready_line_ids.length;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Godkend og send ${group.next_statement_no ?? 'opgørelsen'}`}
      description={`${group.agreement?.partner?.name ?? 'Partneren'} · ${ready} linje${ready === 1 ? '' : 'r'} · ${formatCurrency(group.summary.ready_excl_vat)} ekskl. moms`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={approve.isPending}>
            Annullér
          </Button>
          <Button
            onClick={() => approve.mutate()}
            loading={approve.isPending}
            disabled={addresses(fields.to).length === 0}
          >
            Godkend og send
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Alert tone="info">
          Linjerne markeres som afregnet og kan ikke afregnes igen. Opgørelsen vedhæftes som{' '}
          <span className="font-medium">
            {commissionStatementFileName(group.next_statement_no ?? '')}
          </span>
          .
        </Alert>
        {error ? <Alert tone="error">{error}</Alert> : null}
        <EmailFields sender={sender} draft={fields} onChange={setFields} />
      </div>
    </Modal>
  );
}

function ResendModal({
  statement,
  sender,
  onClose,
  onDone,
}: {
  statement: PartnerStatement;
  sender: string | null;
  onClose: () => void;
  onDone: (text: string, tone: 'success' | 'warning') => void;
}) {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<ReturnType<typeof toFields> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: draft, isLoading } = useQuery({
    queryKey: [...QUERY_KEY, 'statement', statement.id, 'email'],
    queryFn: () =>
      api.get<CommissionEmailDraft>(`/partner-commission/statements/${statement.id}/email`),
  });

  useEffect(() => {
    if (draft && !fields) setFields(toFields(draft));
  }, [draft, fields]);

  const send = useMutation({
    mutationFn: () =>
      api.post<{ sent: boolean; error: string | null }>(
        `/partner-commission/statements/${statement.id}/send`,
        { email: fromFields(fields!) },
      ),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      if (response.sent) {
        const outcome = sendOutcome(statement.statement_no, response, addresses(fields!.to));
        onDone(outcome.text, outcome.tone);
      } else {
        setError(response.error ?? 'Mailen kunne ikke sendes.');
      }
    },
    onError: (cause) => setError(errorText(cause, 'Opgørelsen kunne ikke sendes.')),
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Send ${statement.statement_no} igen`}
      description={`${statement.partner_name} · ${formatCurrency(statement.total_commission_excl_vat)} ekskl. moms`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={send.isPending}>
            Annullér
          </Button>
          <Button
            onClick={() => send.mutate()}
            loading={send.isPending}
            disabled={!fields || addresses(fields.to).length === 0}
          >
            Send
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {isLoading || !fields ? (
          <Skeleton className="h-48" />
        ) : (
          <EmailFields sender={sender} draft={fields} onChange={setFields} />
        )}
      </div>
    </Modal>
  );
}

const SEND_FILTER_LABELS: Record<PartnerStatementSendFilter, string> = {
  sent: 'Sendt',
  not_sent: 'Ikke sendt',
};

/**
 * The approved statements, 25 at a time, newest first - paged and filtered by the server, since
 * the history grows by a statement per partner per month. Filters: partner or number, the month in
 * the number, and whether it went out.
 */
function StatementsCard({
  onResend,
}: {
  /** Null without 'Godkend provision' - then there is no "Send igen". */
  onResend: ((statement: PartnerStatement) => void) | null;
}) {
  const canExport = useAuth().can('partner_commission.export');
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState('');
  const [send, setSend] = useState<PartnerStatementSendFilter | ''>('');
  const [offset, setOffset] = useState(0);
  const q = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);
  const filtered = Boolean(q || month || send);

  const { data, isLoading, error } = useQuery({
    queryKey: [...QUERY_KEY, 'statements', { q, month, send, offset }],
    queryFn: () =>
      api.get<Paginated<PartnerStatement>>('/partner-commission/statements', {
        offset,
        limit: PAGE_SIZE,
        q,
        month,
        send,
      }),
    placeholderData: keepPreviousData,
  });
  const statements = data?.data ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle>
          Opgørelser
          {data && data.total > 0 ? (
            <span className="ml-2 text-sm font-normal text-ink-muted tabular">{data.total}</span>
          ) : null}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="w-56"
            placeholder="Søg på partner eller nummer"
            aria-label="Søg i opgørelserne"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              /* A new filter starts on the first page - page 3 of a shorter list may not exist. */
              setOffset(0);
            }}
          />
          <Input
            type="month"
            className="w-40"
            aria-label="Måned"
            value={month}
            onChange={(event) => {
              setMonth(event.target.value);
              setOffset(0);
            }}
          />
          <Select
            className="w-32"
            aria-label="Afsendelse"
            value={send}
            onChange={(event) => {
              setSend(event.target.value as PartnerStatementSendFilter | '');
              setOffset(0);
            }}
          >
            <option value="">Alle</option>
            {PARTNER_STATEMENT_SEND_FILTERS.map((value) => (
              <option key={value} value={value}>
                {SEND_FILTER_LABELS[value]}
              </option>
            ))}
          </Select>
        </div>
      </CardHeader>
      {isLoading ? (
        <Skeleton className="m-4 h-24" />
      ) : error ? (
        <Alert tone="error" className="m-4">
          {errorText(error, 'Kunne ikke hente opgørelserne.')}
        </Alert>
      ) : statements.length === 0 ? (
        <EmptyState
          title={
            data && data.total > 0
              ? 'Ingen opgørelser på denne side'
              : filtered
                ? 'Ingen opgørelser matcher filtrene'
                : 'Ingen opgørelser endnu'
          }
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Opgørelse</Th>
              <Th>Partner</Th>
              <Th className="w-[170px]">Godkendt</Th>
              <Th className="w-[70px] text-right">Linjer</Th>
              <Th className="w-[140px] text-right">Provision</Th>
              <Th>Afsendelse</Th>
              <Th className="w-[200px]" />
            </tr>
          </thead>
          <tbody>
            {statements.map((statement) => (
              <Tr key={statement.id}>
                <Td className="tabular font-medium">{statement.statement_no}</Td>
                <Td>{statement.partner_name}</Td>
                <Td className="text-xs">
                  {formatDateTime(statement.approved_at)}
                  {statement.approved_by ? (
                    <span className="block text-ink-muted">{statement.approved_by}</span>
                  ) : null}
                </Td>
                <Td className="text-right tabular">{statement.line_count}</Td>
                <Td className="text-right">
                  <Money value={statement.total_commission_excl_vat} />
                </Td>
                <Td className="text-xs">
                  {statement.send_error ? (
                    <>
                      <Badge tone="danger">Ikke sendt</Badge>
                      <span className="mt-1 block text-ink-muted">{statement.send_error}</span>
                    </>
                  ) : statement.sent_at ? (
                    <>
                      <Badge tone="success">Sendt {formatDate(statement.sent_at)}</Badge>
                      <span className="mt-1 block text-ink-muted">
                        {statement.sent_to.join(', ')}
                      </span>
                    </>
                  ) : (
                    <Badge tone="warning">Ikke sendt</Badge>
                  )}
                </Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    {canExport ? (
                      <a href={apiUrl(`/partner-commission/statements/${statement.id}/excel`)}>
                        <Button size="sm" variant="secondary">
                          <Download className="h-3.5 w-3.5" />
                          Excel
                        </Button>
                      </a>
                    ) : null}
                    {onResend ? (
                      <Button size="sm" variant="secondary" onClick={() => onResend(statement)}>
                        Send igen
                      </Button>
                    ) : null}
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {data ? (
        <Pagination total={data.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
      ) : null}
    </Card>
  );
}

/**
 * Mailopsætning, behind its button in the page header: who the statement is copied to, and the
 * text it goes out with. The subject and the text are templates - the placeholders are filled in
 * per statement, and the result can still be edited before each send. The sender is only shown: it
 * is set on /admin/integrationer.
 *
 * Below the fields, every placeholder with what the partner will read in its place - see
 * `COMMISSION_EMAIL_PLACEHOLDER_HELP`.
 */
function EmailSettingsModal({
  settings,
  sender,
  onClose,
  onSaved,
}: {
  settings: CommissionEmailSettings;
  sender: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState(() => ({
    cc: settings.cc.join(', '),
    subject: settings.subject,
    body: settings.body,
  }));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.put<CommissionEmailSettings>('/partner-commission/settings', {
        cc: addresses(fields.cc),
        subject: fields.subject,
        body: fields.body,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData<PartnerCommissionOverview>(QUERY_KEY, (previous) =>
        previous ? { ...previous, email_settings: saved } : previous,
      );
      /* The drafts on the partners are filled in from the settings - fetch them again. */
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      onSaved();
    },
    onError: (cause) => setError(errorText(cause, 'Mailopsætningen kunne ikke gemmes.')),
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Mailopsætning"
      description="Bruges til hver opgørelse og kan rettes, før den sendes."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Annullér
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Gem mailopsætning
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <SenderLine sender={sender} />
        <div>
          <Label htmlFor="settings-cc">Kopi</Label>
          <Input
            id="settings-cc"
            value={fields.cc}
            onChange={(event) => setFields({ ...fields, cc: event.target.value })}
            placeholder="henrik@onecode.dk"
          />
          <p className="mt-1 text-xs text-ink-muted">Altid med. Flere adskilles med komma.</p>
        </div>
        <div>
          <Label htmlFor="settings-subject">Emne</Label>
          <Input
            id="settings-subject"
            value={fields.subject}
            onChange={(event) => setFields({ ...fields, subject: event.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="settings-body">Tekst</Label>
          <Textarea
            id="settings-body"
            rows={10}
            value={fields.body}
            onChange={(event) => setFields({ ...fields, body: event.target.value })}
          />
        </div>

        <div>
          <p className="field-label">Felter i emne og tekst</p>
          <p className="mb-2 text-xs text-ink-muted">
            Skriv feltet med krøllede parenteser. Det udfyldes for hver opgørelse, så partneren ser
            værdien i stedet.
          </p>
          <dl className="divide-y divide-line rounded-md border border-line text-xs">
            {COMMISSION_EMAIL_PLACEHOLDERS.map((name) => (
              <div key={name} className="grid gap-x-3 gap-y-0.5 px-3 py-2 sm:grid-cols-[130px_1fr]">
                <dt className="font-mono font-medium">{`{${name}}`}</dt>
                <dd>
                  {COMMISSION_EMAIL_PLACEHOLDER_HELP[name].description}
                  <span className="block text-ink-muted">
                    Fx {COMMISSION_EMAIL_PLACEHOLDER_HELP[name].example}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </Modal>
  );
}
